import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../adminconfig";

const JobTable = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    position: "",
    workMode: "",
    workType: "",
    experience: "",
    overview: "",
    responsibility: "",
    qualification: "",
    skills: "",
  });

  const adminToken =
    typeof window !== "undefined"
      ? localStorage.getItem("adminToken")
      : null;

  // ✅ fetchJobs wrapped in useCallback
  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
      });
      setJobs(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      fetchJobs();
    }
  }, [adminToken, fetchJobs]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit form (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        responsibility: formData.responsibility
          ? formData.responsibility.split(",")
          : [],
        qualification: formData.qualification
          ? formData.qualification.split(",")
          : [],
        skills: formData.skills ? formData.skills.split(",") : [],
      };

      if (editingJob) {
        // Update
        await axios.put(
          `${API_BASE_URL}/api/jobs/${editingJob._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${adminToken}` },
          }
        );
      } else {
        // Create
        await axios.post(`${API_BASE_URL}/api/jobs`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
      }

      setShowForm(false);
      setEditingJob(null);
      setFormData({
        position: "",
        workMode: "",
        workType: "",
        experience: "",
        overview: "",
        responsibility: "",
        qualification: "",
        skills: "",
      });
      fetchJobs();
    } catch (err) {
      console.error("Error submitting job:", err);
    }
  };

  // Delete job
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      fetchJobs();
    } catch (err) {
      console.error("Error deleting job:", err);
    }
  };

  // Edit job
  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      position: job.position,
      workMode: job.workMode,
      workType: job.workType,
      experience: job.experience,
      overview: job.overview,
      responsibility: job.responsibility?.join(", "),
      qualification: job.qualification?.join(", "),
      skills: job.skills?.join(", "),
    });
    setShowForm(true);
  };

  if (loading) {
    return <p>Loading jobs...</p>;
  }

  return (
    <div className="p-4 relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Job Openings</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Add Job Opening
        </button>
      </div>

      {/* Table */}
      <table className="min-w-full bg-white border border-gray-300 rounded-lg">
        <thead>
          <tr className="bg-gray-100 text-left border-b border-gray-300">
            <th className="py-2 px-4 border-r">ID</th>
            <th className="py-2 px-4 border-r">Position</th>
            <th className="py-2 px-4 border-r">Work Mode</th>
            <th className="py-2 px-4 border-r">Work Type</th>
            <th className="py-2 px-4 border-r">Experience</th>
            <th className="py-2 px-4 border-r">Overview</th>
            <th className="py-2 px-4 border-r">Skills</th>
            <th className="py-2 px-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <tr key={job._id} className="border-b border-gray-200">
                <td className="py-2 px-4 border-r">{job._id}</td>
                <td className="py-2 px-4 border-r">{job.position}</td>
                <td className="py-2 px-4 border-r">{job.workMode}</td>
                <td className="py-2 px-4 border-r">{job.workType}</td>
                <td className="py-2 px-4 border-r">{job.experience}</td>
                <td className="py-2 px-4 border-r">{job.overview}</td>
                <td className="py-2 px-4">
                  {job.skills && job.skills.join(", ")}
                </td>
                <td className="py-2 px-4 flex gap-2">
                  <button
                    onClick={() => handleEdit(job)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(job._id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="py-2 px-4 text-center" colSpan="8">
                No jobs found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Modal Form */}
      {/* Add Job Opening Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] relative z-50">
            <h3 className="text-lg font-bold mb-4">
              {editingJob ? "Edit Job" : "Add Job"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Position"
                className="w-full border px-3 py-2 rounded"
                required
              />
              <input
                type="text"
                name="workMode"
                value={formData.workMode}
                onChange={handleChange}
                placeholder="Work Mode"
                className="w-full border px-3 py-2 rounded"
                required
              />
              <input
                type="text"
                name="workType"
                value={formData.workType}
                onChange={handleChange}
                placeholder="Work Type"
                className="w-full border px-3 py-2 rounded"
                required
              />
              <input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                placeholder="Experience"
                className="w-full border px-3 py-2 rounded"
                required
              />
              <textarea
                name="overview"
                value={formData.overview}
                onChange={handleChange}
                placeholder="Overview"
                className="w-full border px-3 py-2 rounded"
                required
              />
              <input
                type="text"
                name="responsibility"
                value={formData.responsibility}
                onChange={handleChange}
                placeholder="Responsibilities (comma separated)"
                className="w-full border px-3 py-2 rounded"
              />
              <input
                type="text"
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
                placeholder="Qualifications (comma separated)"
                className="w-full border px-3 py-2 rounded"
              />
              <input
                type="text"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                placeholder="Skills (comma separated)"
                className="w-full border px-3 py-2 rounded"
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingJob(null);
                  }}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  {editingJob ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default JobTable;
